import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

type Kind = "info" | "success" | "error";
interface Toast { id: number; kind: Kind; message: string; }

interface Ctx {
  toasts: Toast[];
  push: (message: string, kind?: Kind) => void;
  remove: (id: number) => void;
}

const C = createContext<Ctx | null>(null);
let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const remove = useCallback((id: number) => setToasts((xs) => xs.filter((t) => t.id !== id)), []);
  const push = useCallback((message: string, kind: Kind = "info") => {
    const id = ++seq;
    setToasts((xs) => [...xs, { id, kind, message }]);
    setTimeout(() => remove(id), 3800);
  }, [remove]);

  return (
    <C.Provider value={{ toasts, push, remove }}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`} role="status" onClick={() => remove(t.id)}>
            <span className="toast-dot" aria-hidden="true" />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
      <style>{`
        .toast-host { position: fixed; bottom: var(--s-6); right: var(--s-5); z-index: 200; display: flex; flex-direction: column; gap: var(--s-2); }
        .toast {
          padding: 12px 18px;
          min-width: 240px; max-width: 360px;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--r-md); box-shadow: var(--shadow-lg);
          display: flex; align-items: center; gap: var(--s-3);
          font-size: 13px; font-weight: 500;
          animation: toast-in 240ms var(--ease-out);
          cursor: pointer;
        }
        .toast-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--text-muted); }
        .toast-success .toast-dot { background: var(--success); }
        .toast-success { border-left: 3px solid var(--success); }
        .toast-error .toast-dot   { background: var(--danger);  }
        .toast-error   { border-left: 3px solid var(--danger); }
        .toast-info .toast-dot    { background: var(--primary); }
        .toast-info    { border-left: 3px solid var(--primary); }
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </C.Provider>
  );
}

export function useToast() {
  const v = useContext(C);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v;
}
